import { type ReactElement, useId } from 'react';
import { cn, EyebrowLabel } from '@/shared/ui';

interface ColorOptionView {
  readonly id: string;
  readonly name: string;
  readonly colorCode: string;
  readonly isAvailable: boolean;
}

interface ColorSelectProps {
  readonly colors: readonly ColorOptionView[];
  readonly value: string;
  readonly onChange: (colorOptionId: string) => void;
}

/** 흰 바탕에서 경계가 사라지는 밝은 색인지 — 흰색 계열 견본만 테두리를 올린다(상품 상세 색상 견본과 같은 규칙). */
const isNearWhite = (colorCode: string): boolean =>
  ['#fff', '#ffffff', '#fefefe'].includes(colorCode.trim().toLowerCase());

/**
 * 제품 색상 — 상품 상세와 같은 원형 견본 줄. 견본마다 라디오라 방향키로 옮기며 고른다.
 * 공급 불가 색상은 목록에 남기고 선택만 막는다(정책 §4) — 이름에 취소선을 긋는다.
 */
export const ColorSelect = ({ colors, value, onChange }: ColorSelectProps): ReactElement => {
  const labelId = useId();
  const name = useId();
  const current = colors.find((color) => color.id === value);

  return (
    <fieldset aria-labelledby={labelId} className="space-y-3">
      <div className="space-y-2">
        <EyebrowLabel>Color</EyebrowLabel>
        <p id={labelId} className="text-sm font-semibold">
          색상 <span className="font-normal text-muted-foreground">{current?.name ?? '선택'}</span>
        </p>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-2">
        {colors.map((color) => {
          const isSelected = color.id === value;
          return (
            <label
              key={color.id}
              title={color.isAvailable ? color.name : `${color.name} (공급 불가)`}
              className={cn(
                'flex min-w-12 flex-col items-center gap-1',
                color.isAvailable ? 'cursor-pointer' : 'cursor-not-allowed',
              )}
            >
              <input
                type="radio"
                name={name}
                value={color.id}
                aria-label={color.isAvailable ? color.name : `${color.name} (공급 불가)`}
                checked={isSelected}
                disabled={!color.isAvailable}
                className="peer sr-only"
                onChange={() => onChange(color.id)}
              />
              <span
                aria-hidden
                style={{ backgroundColor: color.colorCode }}
                className={cn(
                  'size-8 rounded-full border border-foreground/20 transition-shadow peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2',
                  isNearWhite(color.colorCode) && 'border-input',
                  isSelected && 'ring-2 ring-foreground ring-offset-2',
                  !color.isAvailable && 'opacity-50',
                )}
              />
              <span
                aria-hidden
                className={cn(
                  'text-xs whitespace-nowrap text-muted-foreground',
                  isSelected && 'font-semibold text-foreground',
                  !color.isAvailable && 'line-through',
                )}
              >
                {color.name}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
};
